import { useState, useEffect } from 'react';
import { api, formatApiError } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  LifeBuoy, Search, ArrowLeft, Send, Clock, CheckCircle2, AlertCircle,
  Loader2, ChevronLeft, ChevronRight, User, ArrowUpCircle,
} from 'lucide-react';

const STATUS_CONFIG = {
  open: { label: 'Abierto', color: 'bg-blue-500/10 text-blue-400', icon: AlertCircle },
  in_progress: { label: 'En Proceso', color: 'bg-amber-500/10 text-amber-400', icon: Clock },
  resolved: { label: 'Resuelto', color: 'bg-emerald-500/10 text-emerald-400', icon: CheckCircle2 },
  closed: { label: 'Cerrado', color: 'bg-zinc-500/10 text-zinc-500', icon: CheckCircle2 },
};

const CATEGORY_LABELS = {
  general: 'General', billing: 'Facturacion', technical: 'Tecnico',
  complaint: 'Queja', suggestion: 'Sugerencia',
};

const PRIORITY_CONFIG = {
  low: { label: 'Baja', color: 'text-zinc-500' },
  normal: { label: 'Normal', color: 'text-blue-400' },
  high: { label: 'Alta', color: 'text-amber-400' },
  urgent: { label: 'Urgente', color: 'text-red-400' },
};

export default function AdminSupportPage() {
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => { loadTickets(); }, [page, statusFilter, categoryFilter]);

  const loadTickets = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (categoryFilter !== 'all') params.category = categoryFilter;
      if (search.trim()) params.search = search.trim();
      const data = await api.adminListTickets(params);
      setTickets(data.tickets || []);
      setTotal(data.total || 0);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const handleSearch = (e) => { e.preventDefault(); setPage(1); loadTickets(); };

  const openTicketDetail = async (ticketId) => {
    setDetailLoading(true);
    try {
      const data = await api.getTicket(ticketId);
      setSelectedTicket(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDetailLoading(false); }
  };

  const handleReply = async () => {
    if (!replyText.trim() || !selectedTicket) return;
    setSending(true);
    try {
      await api.replyTicket(selectedTicket.id, { message: replyText });
      setReplyText('');
      openTicketDetail(selectedTicket.id);
      toast.success('Respuesta enviada');
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  const changeStatus = async (status) => {
    if (!selectedTicket) return;
    try {
      await api.adminChangeTicketStatus(selectedTicket.id, { status });
      toast.success(`Status cambiado a ${STATUS_CONFIG[status]?.label || status}`);
      openTicketDetail(selectedTicket.id);
      loadTickets();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const changePriority = async (priority) => {
    if (!selectedTicket) return;
    try {
      await api.adminChangeTicketPriority(selectedTicket.id, { priority });
      toast.success(`Prioridad cambiada a ${PRIORITY_CONFIG[priority]?.label || priority}`);
      openTicketDetail(selectedTicket.id);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Detail view
  if (selectedTicket) {
    const sc = STATUS_CONFIG[selectedTicket.status] || STATUS_CONFIG.open;
    const pc = PRIORITY_CONFIG[selectedTicket.priority] || PRIORITY_CONFIG.normal;
    return (
      <div className="p-6 lg:p-8 max-w-[1400px] mx-auto" data-testid="admin-ticket-detail">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => { setSelectedTicket(null); loadTickets(); }}
            className="p-2 rounded-lg hover:bg-white/5 text-zinc-400" data-testid="admin-back-to-tickets">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-semibold text-zinc-100 truncate">{selectedTicket.subject}</h1>
            <div className="flex items-center gap-3 mt-1 flex-wrap">
              <span className={`text-[10px] px-2 py-0.5 rounded-full ${sc.color}`}>{sc.label}</span>
              <span className="text-[10px] text-zinc-600">{CATEGORY_LABELS[selectedTicket.category]}</span>
              <span className={`text-[10px] ${pc.color}`}>{pc.label}</span>
              <span className="text-[10px] text-zinc-700">
                {selectedTicket.user_name} ({selectedTicket.user_email})
              </span>
            </div>
          </div>
        </div>

        {/* Admin controls */}
        <div className="flex flex-wrap gap-3 mb-6 p-4 rounded-xl border border-white/[0.04] bg-[#0A0A0D]">
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-zinc-600 uppercase tracking-wider">Status:</span>
            <Select value={selectedTicket.status} onValueChange={changeStatus}>
              <SelectTrigger className="w-32 h-7 text-[10px] bg-transparent border-white/5" data-testid="admin-ticket-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#121215] border-white/10">
                <SelectItem value="open">Abierto</SelectItem>
                <SelectItem value="in_progress">En Proceso</SelectItem>
                <SelectItem value="resolved">Resuelto</SelectItem>
                <SelectItem value="closed">Cerrado</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-zinc-600 uppercase tracking-wider">Prioridad:</span>
            <Select value={selectedTicket.priority} onValueChange={changePriority}>
              <SelectTrigger className="w-28 h-7 text-[10px] bg-transparent border-white/5" data-testid="admin-ticket-priority">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#121215] border-white/10">
                <SelectItem value="low">Baja</SelectItem>
                <SelectItem value="normal">Normal</SelectItem>
                <SelectItem value="high">Alta</SelectItem>
                <SelectItem value="urgent">Urgente</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Messages */}
        <div className="space-y-3 max-h-[450px] overflow-y-auto pr-2 mb-6" data-testid="admin-ticket-messages">
          {detailLoading ? (
            <div className="text-center py-8"><Loader2 className="w-5 h-5 text-zinc-600 animate-spin mx-auto" /></div>
          ) : (selectedTicket.messages || []).map((msg) => {
            const isAdmin = msg.sender_role === 'admin';
            return (
              <div key={msg.id} className={`flex ${isAdmin ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[75%] rounded-xl p-3 ${
                  isAdmin ? 'bg-red-500/5 border border-red-500/10' : 'bg-white/[0.04] border border-white/[0.06]'
                }`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[10px] font-medium ${isAdmin ? 'text-red-400' : 'text-zinc-400'}`}>
                      {isAdmin ? `Admin: ${msg.sender_name}` : msg.sender_name}
                    </span>
                    <span className="text-[10px] text-zinc-700">
                      {new Date(msg.created_at).toLocaleString('es-MX', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  <p className="text-sm text-zinc-200 whitespace-pre-wrap">{msg.content}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Reply (admin) */}
        {selectedTicket.status !== 'closed' && (
          <div className="flex gap-2" data-testid="admin-reply-section">
            <Input
              value={replyText} onChange={e => setReplyText(e.target.value)}
              placeholder="Responder como admin..."
              className="flex-1 bg-white/[0.03] border-white/5 text-zinc-100 h-10 text-sm"
              onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleReply()}
              data-testid="admin-reply-input"
            />
            <Button onClick={handleReply} disabled={sending || !replyText.trim()}
              className="bg-red-600 hover:bg-red-500 text-white h-10 px-4" data-testid="admin-send-reply-btn">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        )}
      </div>
    );
  }

  // Tickets list
  return (
    <div className="p-6 lg:p-8 max-w-[1400px] mx-auto" data-testid="admin-support-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-zinc-100">Soporte / Tickets</h1>
          <p className="text-xs text-zinc-600">{total} tickets en total</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <form onSubmit={handleSearch} className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
            <Input placeholder="Buscar por asunto, nombre o email..." value={search} onChange={e => setSearch(e.target.value)}
              className="pl-10 bg-white/[0.03] border-white/5 text-zinc-100 h-9 text-sm" data-testid="admin-tickets-search" />
          </div>
        </form>
        <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-36 bg-white/[0.03] border-white/5 text-zinc-300 h-9 text-sm">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-[#121215] border-white/10">
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="open">Abiertos</SelectItem>
            <SelectItem value="in_progress">En Proceso</SelectItem>
            <SelectItem value="resolved">Resueltos</SelectItem>
            <SelectItem value="closed">Cerrados</SelectItem>
          </SelectContent>
        </Select>
        <Select value={categoryFilter} onValueChange={v => { setCategoryFilter(v); setPage(1); }}>
          <SelectTrigger className="w-36 bg-white/[0.03] border-white/5 text-zinc-300 h-9 text-sm">
            <SelectValue placeholder="Categoria" />
          </SelectTrigger>
          <SelectContent className="bg-[#121215] border-white/10">
            <SelectItem value="all">Todas</SelectItem>
            <SelectItem value="general">General</SelectItem>
            <SelectItem value="billing">Facturacion</SelectItem>
            <SelectItem value="technical">Tecnico</SelectItem>
            <SelectItem value="complaint">Queja</SelectItem>
            <SelectItem value="suggestion">Sugerencia</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-white/[0.04] bg-[#0A0A0D] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/[0.04]">
                <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider font-medium">Ticket</th>
                <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider font-medium">Usuario</th>
                <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider font-medium">Categoria</th>
                <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider font-medium">Prioridad</th>
                <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider font-medium">Status</th>
                <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider font-medium">Actualizado</th>
                <th className="text-right px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider font-medium">Accion</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="text-center py-12"><Loader2 className="w-5 h-5 text-zinc-600 animate-spin mx-auto" /></td></tr>
              ) : tickets.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-12 text-zinc-600 text-sm">Sin tickets</td></tr>
              ) : tickets.map(t => {
                const sc = STATUS_CONFIG[t.status] || STATUS_CONFIG.open;
                const pc = PRIORITY_CONFIG[t.priority] || PRIORITY_CONFIG.normal;
                return (
                  <tr key={t.id} className="border-b border-white/[0.02] hover:bg-white/[0.01] transition-colors" data-testid={`admin-ticket-row-${t.id}`}>
                    <td className="px-4 py-3">
                      <p className="text-zinc-200 text-xs font-medium truncate max-w-[200px]">{t.subject}</p>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-zinc-300 text-xs">{t.user_name}</p>
                        <p className="text-zinc-700 text-[10px]">{t.user_email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-[10px] text-zinc-500">{CATEGORY_LABELS[t.category]}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] font-medium ${pc.color}`}>
                        {t.priority === 'urgent' && <ArrowUpCircle className="w-3 h-3 inline mr-0.5" />}
                        {pc.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${sc.color}`}>{sc.label}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-[10px] text-zinc-600">
                        {new Date(t.updated_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" variant="ghost" onClick={() => openTicketDetail(t.id)}
                        className="h-7 text-[10px] text-zinc-300 hover:bg-white/5" data-testid={`open-ticket-${t.id}`}>
                        Abrir
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-white/[0.04]">
          <span className="text-xs text-zinc-600">Pagina {page} de {Math.ceil(total / 20) || 1}</span>
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" disabled={page === 1} onClick={() => setPage(p => p - 1)} className="h-7 text-xs text-zinc-400">
              <ChevronLeft className="w-3 h-3" />
            </Button>
            <Button size="sm" variant="ghost" disabled={tickets.length < 20} onClick={() => setPage(p => p + 1)} className="h-7 text-xs text-zinc-400">
              <ChevronRight className="w-3 h-3" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
